import { useState, useMemo } from "react";
import { X, Swords, FlaskConical, HardHat, Beer, Building2, ChevronRight, Hammer, Lock, ArrowUpCircle, Skull } from "lucide-react";
import { SpaceHomeBtn } from "./SpaceNav";
import DebrisRun from "./DebrisRun";
import CorridorRun from "./CorridorRun";
import { sfx } from "./audio";
import { loadState, addScrap, upgradeCommandCentre, displayableHeroes, descendUnlocked, prologueRoster, SCRAP_TO_CC2,
  pullHero, buyFlashDrive, advanceCorridorStage, SUMMON_POOL, FLASH_DRIVE_SCRAP_COST, convertShards, SHARDS_PER_DRIVE } from "./stateManager";

const B = "/game/bunker";
const TIER_COLORS = { Blue: "#4fc3ff", Purple: "#c04bff", Gold: "#f5c542" };

// Tappable rooms overlaid on the honeycomb bunker map.
const ROOMS = [
  { key: "command", label: "Command Centre", top: "44%", left: "50%", Icon: Building2, color: "#4fc3ff",
    desc: "The nerve centre. Upgrade the Command Centre to unlock the main blast door and, later, the surface.", interior: true },
  { key: "biofood", label: "Bio-Food Lab", top: "24%", left: "24%", Icon: FlaskConical, color: "#37e0a0",
    desc: "Cultivates Bio-Food to sustain the fireteam and fuel 24-hour bunker shields." },
  { key: "metal", label: "Ship Metal Room", top: "24%", left: "76%", Icon: HardHat, color: "#f5c542",
    desc: "Salvages and refines Ship Metal — the backbone of every upgrade and defensive shield." },
  { key: "canteen", label: "Canteen / Bar", top: "72%", left: "28%", Icon: Beer, color: "#ff8a5c",
    desc: "Morale hub. Spend a Golden Flash Drive here to recruit new heroes to the roster." },
  { key: "armoury", label: "Armoury", top: "72%", left: "72%", Icon: Swords, color: "#ff6a6a",
    desc: "Rearm the squad and prep loadouts before descending into the next corridor sector." },
];

export default function BunkerHub({ onBack, onDeploy }) {
  const [room, setRoom] = useState(null);
  const [debris, setDebris] = useState(false);
  const [state, setState] = useState(() => loadState());
  const [modal, setModal] = useState(null); // 'alpha_slice_gate' | 'need_cc2'
  const [view, setView] = useState("hub");   // hub | corridor
  const [gacha, setGacha] = useState(false);
  const [pullResult, setPullResult] = useState(null);

  const p = state.prologue;
  const scrap = p.resources.scrap;
  const cc = p.commandCentreLevel;
  const canDescend = descendUnlocked(state);
  const active = displayableHeroes(state);
  const roster = prologueRoster(state);
  const mia = Object.values(roster).filter((h) => h.status === "kidnapped_stage_45");
  const g = p.gacha || { drives: 0, first_done: false };
  const unlocked = p.unlocked_heroes || [];
  const corridorStage = p.corridorStage || 4;
  const hasBlaze = unlocked.includes("blaze");
  const corridorRoster = useMemo(() => ["jess", "nancy", ...(hasBlaze ? ["blaze"] : [])], [hasBlaze]);
  const corridorReward = 30 + corridorStage * 3 + (corridorStage % 5 === 0 ? 100 : 0);

  const doPull = () => {
    const r = pullHero(loadState());
    if (r.ok) { setState(r.state); setPullResult({ hero: r.hero, dupe: r.dupe, shardGain: r.shardGain }); if (r.hero.tier === "Gold") sfx.victory(); else sfx.cast(); }
  };
  const doBuyDrive = () => { const r = buyFlashDrive(loadState()); if (r.ok) setState(r.state); };
  const doConvert = () => { const r = convertShards(loadState()); if (r.ok) setState(r.state); };
  const onCorridorWin = () => { setState(advanceCorridorStage(loadState(), corridorReward)); setView("hub"); };

  const onDebrisDone = (gained) => {
    setState(addScrap(loadState(), gained));
    setDebris(false);
  };

  const doUpgradeCC = () => {
    const res = upgradeCommandCentre(loadState());
    if (res.blocked === "alpha_slice_gate") { setModal("alpha_slice_gate"); return; }
    if (res.blocked === "insufficient_scrap") { setModal("need_cc2"); return; }
    if (res.ok) { setState(res.state); }
  };

  const tryDescend = () => {
    if (!canDescend) { setModal("need_cc2"); return; }
    setView("corridor");
  };

  const scrapPct = Math.min(100, Math.round((scrap / SCRAP_TO_CC2) * 100));

  if (view === "corridor") {
    return <CorridorRun stage={corridorStage} roster={corridorRoster} reward={corridorReward} onWin={onCorridorWin} onExit={() => setView("hub")} />;
  }

  return (
    <div className="ss-viewport bhub" data-testid="bunker-hub">
      <div className="ss-atmosphere" />
      <SpaceHomeBtn onClick={onBack} testid="bunker-hub-back" />

      <div className="bhub-head" data-testid="bunker-hub-head">
        <span className="bhub-title">BUNKER HUB</span>
        <span className="bhub-sub">Sublevel Command · CC Lv {cc} · Sectors 1–60</span>
      </div>

      {/* Survivor roster strip — active only (kidnapped/locked filtered out) */}
      <div data-testid="bunker-roster-strip" style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", padding: "0 12px 6px" }}>
        {active.map((h) => (
          <div key={h.key} data-testid={`bunker-hero-${h.key}`} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999,
            background: "rgba(10,16,28,0.8)", border: `1px solid ${TIER_COLORS[h.tier] || "#4fc3ff"}`, fontSize: 11, color: "#e6eefb",
          }}>
            <b style={{ color: TIER_COLORS[h.tier] || "#4fc3ff" }}>{h.name}</b>
            <span style={{ color: "#8fa2bd" }}>Lv{h.level}</span>
          </div>
        ))}
        {mia.map((h) => (
          <div key={h.key} data-testid={`bunker-hero-mia-${h.key}`} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999,
            background: "rgba(10,16,28,0.6)", border: "1px dashed #ff6a6a", fontSize: 11, color: "#ffb0b0", opacity: 0.85,
          }}>
            <Skull size={12} /> <b>{h.name}</b> <span style={{ fontSize: 10 }}>MIA · Sector 45</span>
          </div>
        ))}
        {unlocked.map((k) => {
          const h = SUMMON_POOL.find((x) => x.key === k);
          if (!h) return null;
          return (
            <div key={k} data-testid={`bunker-hero-summoned-${k}`} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999,
              background: "rgba(10,16,28,0.8)", border: `1px solid ${TIER_COLORS[h.tier] || "#4fc3ff"}`, fontSize: 11, color: "#e6eefb",
            }}>
              <b style={{ color: TIER_COLORS[h.tier] || "#4fc3ff" }}>{h.name}</b>
              <span style={{ color: "#8fa2bd" }}>{h.weapon}</span>
            </div>
          );
        })}
      </div>

      <div className="bhub-map-wrap">
        <div className="bhub-map">
          <img src={`${B}/bunker_map.jpg`} alt="Bunker complex" className="bhub-map-img" data-testid="bunker-hub-map" />
          {ROOMS.map((r) => (
            <button key={r.key} className="bhub-node" data-testid={`bunker-room-${r.key}`}
              style={{ top: r.top, left: r.left, "--nc": r.color }} onClick={() => r.key === "canteen" ? setGacha(true) : setRoom(r)}>
              <r.Icon size={15} />
              <span className="bhub-node-label">{r.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Rebuild loop — Salvage run + Command Centre upgrade gate */}
      <div data-testid="bunker-rebuild-panel" style={{ padding: "0 12px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          <button data-testid="bunker-clear-debris" onClick={() => setDebris(true)}
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", borderRadius: 12,
              background: "rgba(245,197,66,0.14)", border: "1px solid rgba(245,197,66,0.6)", color: "#ffe08a", fontWeight: 800, cursor: "pointer" }}>
            <Hammer size={16} /> Clear Debris · Salvage Run
          </button>
        </div>
        <div style={{ background: "rgba(10,16,28,0.8)", border: "1px solid rgba(79,195,255,0.28)", borderRadius: 12, padding: "10px 12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#cfe0f5", marginBottom: 6 }}>
            <span>Command Centre <b style={{ color: "#4fc3ff" }}>Lv {cc}</b></span>
            <span data-testid="bunker-scrap">🔩 {scrap} / {SCRAP_TO_CC2} Scrap</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden", marginBottom: 8 }}>
            <div style={{ height: "100%", width: `${scrapPct}%`, background: "linear-gradient(90deg,#4fc3ff,#37e0a0)", transition: "width .3s" }} />
          </div>
          {cc < 2 ? (
            <button data-testid="bunker-upgrade-cc" onClick={doUpgradeCC} disabled={scrap < SCRAP_TO_CC2}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "10px", borderRadius: 10,
                background: scrap >= SCRAP_TO_CC2 ? "rgba(55,224,160,0.18)" : "rgba(255,255,255,0.05)",
                border: `1px solid ${scrap >= SCRAP_TO_CC2 ? "rgba(55,224,160,0.7)" : "rgba(255,255,255,0.12)"}`,
                color: scrap >= SCRAP_TO_CC2 ? "#8affc9" : "#5b6b82", fontWeight: 800, cursor: scrap >= SCRAP_TO_CC2 ? "pointer" : "not-allowed" }}>
              <ArrowUpCircle size={16} /> Upgrade to Lv 2 (blast door)
            </button>
          ) : (
            <div style={{ fontSize: 11, color: "#8affc9", textAlign: "center" }}>✓ Blast door online — corridor sectors unlocked</div>
          )}
        </div>
      </div>

      <button className="bhub-deploy" data-testid="bunker-hub-deploy" onClick={tryDescend}
        style={!canDescend ? { opacity: 0.55 } : undefined}>
        {canDescend ? <Swords size={16} /> : <Lock size={16} />} Descend · Corridor Sectors <ChevronRight size={16} />
      </button>
      {!canDescend && (
        <div data-testid="bunker-descend-tooltip" style={{ textAlign: "center", fontSize: 11, color: "#ff9a6a", padding: "2px 12px 8px" }}>
          Upgrade the Command Centre to Level 2 to clear the main blast door.
        </div>
      )}

      {/* Room detail */}
      {room && (
        <div className="ss-overlay bhub-room" data-testid="bunker-room-modal" onClick={() => setRoom(null)}>
          <div className="bhub-room-card" onClick={(e) => e.stopPropagation()}>
            <button className="bhub-room-x" data-testid="bunker-room-close" onClick={() => setRoom(null)}><X size={16} /></button>
            {room.interior
              ? <img src={`${B}/command_room.png`} alt="" className="bhub-room-img" />
              : <div className="bhub-room-icon" style={{ color: room.color, borderColor: room.color }}><room.Icon size={34} /></div>}
            <span className="bhub-room-name" style={{ color: room.color }}>{room.label}</span>
            <p className="bhub-room-desc">{room.desc}</p>
            {room.key === "command"
              ? <span className="bhub-room-soon">Command Centre · Level {cc}</span>
              : <span className="bhub-room-soon">Unlocks as you clear corridor sectors</span>}
          </div>
        </div>
      )}

      {/* Salvage minigame */}
      {debris && <DebrisRun onComplete={onDebrisDone} onClose={() => setDebris(false)} />}

      {/* Golden Flash Drive gacha (Canteen) */}
      {gacha && (
        <div className="ss-overlay" data-testid="bunker-gacha" style={{ zIndex: 65, background: "rgba(4,6,12,0.92)" }} onClick={() => { setGacha(false); setPullResult(null); }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "min(94vw,420px)", background: "rgba(10,10,18,0.97)", border: "1px solid rgba(245,197,66,0.5)", borderRadius: 18, padding: 20, textAlign: "center", boxShadow: "0 0 40px rgba(245,197,66,0.2)" }}>
            <button className="bhub-room-x" data-testid="bunker-gacha-close" onClick={() => { setGacha(false); setPullResult(null); }}><X size={16} /></button>
            <div style={{ color: "#ffd86b", fontWeight: 900, fontSize: 16 }}>GOLDEN FLASH DRIVE</div>
            <div style={{ fontSize: 11, color: "#9fb0c8", marginBottom: 14 }}>Canteen / Bar · Hero Summon</div>
            {!pullResult ? (
              <>
                <div style={{ fontSize: 54, marginBottom: 6 }}>💾</div>
                <div data-testid="bunker-gacha-drives" style={{ color: "#e6eefb", marginBottom: 6 }}>Flash Drives: <b style={{ color: "#ffd86b" }}>{g.drives}</b></div>
                <div style={{ display: "flex", justifyContent: "center", gap: 14, fontSize: 11, color: "#9fb0c8", marginBottom: 10 }}>
                  <span data-testid="bunker-gacha-pity">Pity: <b style={{ color: "#c04bff" }}>{g.pity || 0}/10</b></span>
                  <span data-testid="bunker-gacha-shards">Shards: <b style={{ color: "#7fe0ff" }}>{g.shards || 0}</b></span>
                </div>
                {!g.first_done && <div style={{ fontSize: 11, color: "#c04bff", marginBottom: 10 }}>★ First pull guaranteed PURPLE — Blaze the Flame-Thrower</div>}
                <button data-testid="bunker-gacha-pull" onClick={doPull} disabled={g.drives <= 0}
                  style={{ width: "100%", padding: "12px", borderRadius: 12, marginBottom: 8, fontWeight: 900, cursor: g.drives > 0 ? "pointer" : "not-allowed",
                    background: g.drives > 0 ? "linear-gradient(90deg,#f5c542,#ff8a5c)" : "rgba(255,255,255,0.06)", border: "none", color: g.drives > 0 ? "#1a1206" : "#5b6b82" }}>
                  {g.drives > 0 ? "◆ DECRYPT DRIVE — SUMMON" : "No drives — buy one below"}
                </button>
                <button data-testid="bunker-gacha-buy" onClick={doBuyDrive} disabled={scrap < FLASH_DRIVE_SCRAP_COST}
                  style={{ width: "100%", padding: "9px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: scrap >= FLASH_DRIVE_SCRAP_COST ? "pointer" : "not-allowed",
                    background: "rgba(245,197,66,0.12)", border: "1px solid rgba(245,197,66,0.4)", color: scrap >= FLASH_DRIVE_SCRAP_COST ? "#ffe08a" : "#5b6b82" }}>
                  Buy 1 Drive · {FLASH_DRIVE_SCRAP_COST} Scrap (have {scrap})
                </button>
                <button data-testid="bunker-gacha-convert" onClick={doConvert} disabled={(g.shards || 0) < SHARDS_PER_DRIVE}
                  style={{ width: "100%", marginTop: 8, padding: "9px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: (g.shards || 0) >= SHARDS_PER_DRIVE ? "pointer" : "not-allowed",
                    background: "rgba(127,224,255,0.12)", border: "1px solid rgba(127,224,255,0.4)", color: (g.shards || 0) >= SHARDS_PER_DRIVE ? "#bfefff" : "#5b6b82" }}>
                  Convert {SHARDS_PER_DRIVE} Shards → 1 Drive (have {g.shards || 0})
                </button>
              </>
            ) : (
              <div data-testid="bunker-gacha-result">
                <div style={{ fontSize: 11, color: "#9fb0c8" }}>{pullResult.dupe ? `DUPLICATE — +${pullResult.shardGain} shards` : "NEW HERO UNLOCKED"}</div>
                <div style={{ margin: "14px auto", width: 150, height: 150, borderRadius: 16, border: `3px solid ${TIER_COLORS[pullResult.hero.tier]}`, boxShadow: `0 0 34px ${TIER_COLORS[pullResult.hero.tier]}`, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)", fontSize: 52 }}>
                  {pullResult.hero.key === "blaze" ? "🔥" : pullResult.hero.tier === "Gold" ? "⭐" : pullResult.hero.tier === "Purple" ? "✦" : "◆"}
                </div>
                <div style={{ color: TIER_COLORS[pullResult.hero.tier], fontWeight: 900, fontSize: 18 }}>{pullResult.hero.name}</div>
                <div style={{ color: "#e6eefb", fontSize: 12, fontWeight: 700 }}>{pullResult.hero.tier} · {pullResult.hero.weapon}</div>
                <div style={{ color: "#9fb0c8", fontSize: 11, margin: "6px 0 14px" }}>{pullResult.hero.blurb}</div>
                <button className="ss-enter-btn" data-testid="bunker-gacha-again" onClick={() => setPullResult(null)}>{g.drives > 0 ? "Pull Again" : "Done"}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tier-13 surface hard-lock / scrap gate modals */}
      {modal && (
        <div className="ss-overlay" data-testid="bunker-gate-modal" style={{ zIndex: 70, background: "rgba(4,6,12,0.9)" }} onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(92vw,420px)", background: "rgba(10,16,28,0.97)", border: "1px solid rgba(245,197,66,0.5)", borderRadius: 16, padding: 20, textAlign: "center" }}>
            {modal === "alpha_slice_gate" ? (
              <>
                <div style={{ color: "#f5c542", fontWeight: 900, fontSize: 16, marginBottom: 8 }}>Surface Transmission Offline</div>
                <p style={{ fontSize: 13, color: "#cfe0f5", lineHeight: 1.5 }}>Command Centre Level 13 requires Phase B Surface Compound modules. The underground bunker safety parameters are maximized for this version.</p>
              </>
            ) : (
              <>
                <div style={{ color: "#ff9a6a", fontWeight: 900, fontSize: 16, marginBottom: 8 }}>Blast Door Sealed</div>
                <p style={{ fontSize: 13, color: "#cfe0f5", lineHeight: 1.5 }}>Run the salvage loop to gather <b>{SCRAP_TO_CC2} Scrap</b>, then upgrade the Command Centre to Level 2 to clear the main blast door.</p>
              </>
            )}
            <button className="ss-enter-btn" data-testid="bunker-gate-ok" onClick={() => setModal(null)} style={{ marginTop: 14 }}>Understood</button>
          </div>
        </div>
      )}
    </div>
  );
}
